import React, { useRef, useState } from 'react';
import { Box, Grid, Typography, Stack, Accordion, AccordionSummary, AccordionDetails, TextField } from '@mui/material';
import MainCard from 'components/MainCard';
import LoadingButton from 'components/@extended/LoadingButton';
import readerImg from 'assets/images/analytics/reader.svg'; // Adjust path if different
import { DownOutlined, UpOutlined } from '@ant-design/icons';
import { SendSharp } from '@mui/icons-material';
import { useMutation, useQuery } from '@tanstack/react-query';
import { coachSupport } from 'services/support/coachSupport'; // Reuse or adjust to studentSupport service
import toast from 'react-hot-toast';
import { getUserByID } from 'services/users/getUserByID';
import { studentSupport } from 'services/support/studentSupport';

const faqs = [
  {
    q: 'How do I start a yoga session on this platform?',
    a: 'Go to the "My Classes" section, select your desired class, and follow the on-screen instructions to begin your session.'
  },
  {
    q: 'How can I track my progress over time?',
    a: 'Use the "Progress Tracker" feature to view detailed analytics and reports of your yoga practice and improvements.'
  },
  {
    q: 'Where can I find feedback on my asana postures?',
    a: 'After each session, visit the "Reports" section to see AI-generated feedback and suggestions for your asana postures.'
  },
  {
    q: 'Can I access previous session reports?',
    a: 'Yes, all your previous session reports are stored in the "Summary" section for you to review anytime.'
  },
  {
    q: 'What should I do if I face technical issues during a session?',
    a: 'If you encounter any issues, use the support form below to send your query, or check the FAQ for quick solutions.'
  }
];

function Support() {
  const stepsRef = useRef(null);
  const [expanded, setExpanded] = useState(0);
  const [query, setQuery] = useState('');

  const userid = JSON.parse(localStorage.getItem('userid'));
  const roleid = JSON.parse(localStorage.getItem('roleid'));

  const { data: userData } = useQuery({
    queryFn: () => getUserByID({ userid, roleid }),
    queryKey: ['student-user-info', userid],
    enabled: !!userid && !!roleid
  });

  const { mutate: sendQuery, isPending } = useMutation({
    mutationFn: studentSupport,
    onSuccess: (data) => {
      toast.success(data.message || 'Query sent successfully');
      setQuery('');
    },
    onError: (error) => {
      toast.error(error.message || 'Failed to send query');
    }
  });

  const handleGetStarted = () => {
    stepsRef.current?.scrollIntoView({ behavior: 'smooth' });
  };

  const handleAccordionChange = (panel) => (_, isExpanded) => {
    setExpanded(isExpanded ? panel : false);
  };

  const handleSendQuery = () => {
    if (!query.trim()) {
      toast.error('Please enter your query');
      return;
    }

    const senderName = userData?.username || 'Student';
    const senderEmail = userData?.emailaddress || 'unknown@student.com';
    const studentid = userData?.userid;
    const avatar = userData?.avatar || '';

    if (!studentid) {
      toast.error('Student info not available');
      return;
    }

    sendQuery({
      senderName,
      senderEmail,
      queryMessage: query,
      studentid
    });
  };

  return (
    <Box sx={{ minHeight: 'auto', bgcolor: 'background.default', display: 'flex', flexDirection: 'column' }}>
      {/* Hero Section */}
      <MainCard border={false} sx={{ mt: 4, p: { xs: 2, md: 4 }, bgcolor: (theme) => theme.palette.primary.darker }}>
        <Grid container spacing={4} alignItems="center" justifyContent="center">
          <Grid item xs={12} md={6}>
            <Stack spacing={2} alignItems="center">
              <Typography variant="h2" color="white" fontWeight={800} align="center" sx={{ mb: 2 }}>
                How can we help?
              </Typography>
              <Typography variant="h5" color="white" align="center">
                New to the Yoga Platform?
              </Typography>
              <Typography variant="body1" color="white" align="center">
                Start your yoga journey with beginner-friendly resources to enhance your practice.
              </Typography>
              <LoadingButton variant="contained" color="primary" size="large" onClick={handleGetStarted}>
                Get Started
              </LoadingButton>
            </Stack>
          </Grid>
          <Grid item xs={12} md={6} sx={{ textAlign: 'center' }}>
            <Box component="img" src={readerImg} alt="Support Illustration" sx={{ maxWidth: 320, width: '100%', mx: 'auto' }} />
          </Grid>
        </Grid>
      </MainCard>

      {/* FAQ Section */}
      <Box mt={6} mb={6} ref={stepsRef}>
        <MainCard
          title={
            <Typography variant="h4" color="primary" fontWeight={700}>
              Frequently Asked Questions
            </Typography>
          }
        >
          {faqs.map((faq, idx) => (
            <Accordion
              key={idx}
              expanded={expanded === idx}
              onChange={handleAccordionChange(idx)}
              sx={{
                mb: 2,
                boxShadow: 'none',
                border: '1px solid',
                borderColor: 'divider',
                '&:before': { display: 'none' }
              }}
            >
              <AccordionSummary
                expandIcon={
                  expanded === idx ? (
                    <UpOutlined style={{ transform: 'rotate(-90deg)', transition: 'transform 0.2s' }} />
                  ) : (
                    <DownOutlined style={{ transform: 'rotate(0deg)', transition: 'transform 0.2s' }} />
                  )
                }
                aria-controls={`faq-content-${idx}`}
                id={`faq-header-${idx}`}
              >
                <Typography variant="subtitle1" fontWeight={600} color="text.primary">
                  {faq.q}
                </Typography>
              </AccordionSummary>
              <AccordionDetails>
                <Typography variant="body1" color="primary">
                  {faq.a}
                </Typography>
              </AccordionDetails>
            </Accordion>
          ))}
        </MainCard>
      </Box>

      {/* More Queries Section */}
      <Box mb={8}>
        <MainCard>
          <Typography variant="h5" color="primary" fontWeight={700} mb={3}>
            Still need help?
          </Typography>
          <Stack spacing={2}>
            <TextField
              label="Type your question here"
              placeholder="Enter your query..."
              variant="outlined"
              fullWidth
              multiline
              disabled={isPending}
              value={query}
              onChange={(e) => setQuery(e.target.value)}
              minRows={3}
              sx={{
                '& .MuiOutlinedInput-root': { alignItems: 'flex-start' }
              }}
            />
            <LoadingButton
              variant="contained"
              color="primary"
              loading={isPending}
              disabled={isPending}
              onClick={handleSendQuery}
              startIcon={!isPending && <SendSharp />}
              sx={{
                width: { xs: '100%', sm: '120px' },
                alignSelf: 'flex-start'
              }}
            >
              {!isPending && 'Send'}
            </LoadingButton>
          </Stack>
        </MainCard>
      </Box>
    </Box>
  );
}

export default Support;
